import { getJWTToken } from '../utils/auth';
import { authHeaders } from './impersonation';
import { clockSchoolYear } from './schoolYear';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type SchoolYear = {
  label: string;
  displayLabel: string;
  ordinal: number;
  startDate: string;
  endDate: string;
  hasUploads: boolean;
  hasEvaluation: boolean;
};

export type SchoolYears = {
  current: SchoolYear;
  years: SchoolYear[];
};

function asSchoolYear(body: unknown): SchoolYear {
  const y = body as Record<string, unknown> | null;
  if (
    !y ||
    typeof y.label !== 'string' ||
    typeof y.ordinal !== 'number' ||
    typeof y.startDate !== 'string' ||
    typeof y.endDate !== 'string'
  ) {
    throw new Error('school-years response is missing a year');
  }
  return {
    label: y.label,
    displayLabel: typeof y.displayLabel === 'string' ? y.displayLabel : `SY ${y.label}`,
    ordinal: y.ordinal,
    startDate: y.startDate,
    endDate: y.endDate,
    hasUploads: y.hasUploads === true,
    hasEvaluation: y.hasEvaluation === true,
  };
}

export async function fetchSchoolYears(): Promise<SchoolYears> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/program-evaluation/school-years`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`fetch-school-years failed: ${response.status}`);
  }
  const body = (await response.json()) as { current?: unknown; years?: unknown[] } | null;
  return {
    current: asSchoolYear(body?.current),
    years: Array.isArray(body?.years) ? body.years.map(asSchoolYear) : [],
  };
}

let primed: SchoolYear | null = null;
let sessionEpoch = 0;

export function resetSchoolYearsSession(): void {
  sessionEpoch += 1;
  primed = null;
}

export function primeCurrentSchoolYear(year: SchoolYear | null): void {
  primed = year;
}

function primedIsLive(now: Date): boolean {
  if (primed === null) return false;
  const end = new Date(`${primed.endDate}T23:59:59.999Z`);
  const start = new Date(`${primed.startDate}T00:00:00.000Z`);
  return !Number.isNaN(end.getTime()) && !Number.isNaN(start.getTime()) && now >= start && now <= end;
}

export function currentSchoolYear(now: Date = new Date()): string {
  return primedIsLive(now) ? (primed as SchoolYear).label : clockSchoolYear(now);
}

export async function hydrateSchoolYears(): Promise<void> {
  const epoch = sessionEpoch;
  try {
    const years = await fetchSchoolYears();
    if (epoch !== sessionEpoch) return;
    primed = years.current;
  } catch (err) {
    console.warn('school-years hydrate failed; deriving the current year from the clock', err);
  }
}
